import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Stack } from "@mui/material";
import { useForm } from "react-final-form";
import { createListCollection } from "@ark-ui/react";

import { useTranslation } from "@/lib/i18n";
import { TextField } from "@/features/shared/components";
import MapboxStyleSelector from "@/features/shared/components/mapbox-style-selector/MapboxStyleSelector";
import { MapWidgetConfigurationAllOf, MapWidgetConfigurationAllOfLayers } from "@/lib/api";
import MapLayerConfiguration from "@/features/admin/components/map-layer-configuration/MapLayerConfiguration";
import Select from "@/components/select/select";
import { BasicWidgetFormProps } from "@/features/widget-forms/dialogs/widget-dialog-types";
import { mapRegionPresetBes, mapRegionPresetNl } from "@/config/map-region-presets";

const COORDS_PRESETS = {
  NL: mapRegionPresetNl.toBaseLayerCoords(),
  BES: mapRegionPresetBes.toBaseLayerCoords(),
  CUSTOM: undefined,
};

const DEFAULT_LAYER_VALUES: MapWidgetConfigurationAllOfLayers = {
  id: "",
  type: "",
  title: "",
  enabled_by_default: false,
  layer: "",
  style: "",
  wms_dataset_id: "",
  show_legend: true,
};

const DEFAULT_VALUES: MapWidgetConfigurationAllOf = {
  type: "map",
  map_box_style_id: "",
  base_layer_coords: COORDS_PRESETS.NL,
  layers: [DEFAULT_LAYER_VALUES],
};

export function MapWidgetForm({ values, errors }: BasicWidgetFormProps) {
  const { t } = useTranslation();
  console.log(errors);
  const [initialValues] = useState(() => {
    const configuration = values?.configuration;
    if (!configuration) return DEFAULT_VALUES;

    const initialValues = DEFAULT_VALUES;

    if ("base_layer_coords" in configuration) {
      initialValues.base_layer_coords = configuration.base_layer_coords;
    }
    if ("map_box_style_id" in configuration) {
      initialValues.map_box_style_id = configuration.map_box_style_id;
    }
    if ("layers" in configuration) {
      initialValues.layers = configuration.layers;
    }

    return initialValues;
  });

  const form = useForm();
  const coordsCount = useMemo(() => {
    const coordsValue = form.getFieldState("base_layer_coords");
    return Math.max(coordsValue?.length ?? 2, 2);
  }, [form]);

  const formValues = form.getState().values;
  const layers = formValues?.configuration?.layers;
  const layerCount = useMemo(() => {
    if (!layers) return 0;
    return layers.length;
  }, [layers]);

  const removeLayer = useCallback(
    (id: number) => {
      if (layers) {
        const newLayers = [...layers];
        newLayers.splice(id, 1);
        form.change("configuration.layers", newLayers);
      }
    },
    [layers],
  );

  const addLayer = useCallback(() => {
    form.change(`configuration.layers[${layerCount}]`, {});
  }, [layerCount]);

  // Preset options
  const presetCollection = createListCollection({
    items: [
      {
        label: "Custom",
        value: "CUSTOM",
      },
      {
        label: "Nederland",
        value: "NL",
      },
      {
        label: "BES Eilanden",
        value: "BES",
      },
    ],
  });
  const [baseLayerPreset, setBaseLayerPreset] = useState(() => {
    const matchingPreset = Object.entries(COORDS_PRESETS).find(([, value]) => {
      return value?.every(({ lat, lng }, index) => {
        return (
          lat.toString() === initialValues["base_layer_coords"][index]?.lat?.toString() &&
          lng.toString() === initialValues["base_layer_coords"][index]?.lng?.toString()
        );
      });
    });
    return matchingPreset?.[0] ?? "CUSTOM";
  });
  const updateBaseLayerCoords = useCallback(
    (key: string) => {
      const value = COORDS_PRESETS[key as keyof typeof COORDS_PRESETS];
      if (value) {
        form.change("configuration.base_layer_coords", value);
      }
    },
    [form, setBaseLayerPreset],
  );

  const baseLayerCoords = formValues?.configuration?.base_layer_coords;
  useEffect(() => {
    if (!baseLayerCoords) return;
    setBaseLayerPreset(() => {
      const matchingPreset = Object.entries(COORDS_PRESETS).find(([, value]) => {
        return value?.every(({ lat, lng }, index) => {
          return (
            lat.toString() === baseLayerCoords?.[index]?.lat?.toString() &&
            lng.toString() === baseLayerCoords?.[index]?.lng?.toString()
          );
        });
      });
      return matchingPreset?.[0] ?? "CUSTOM";
    });
  }, [baseLayerCoords]);

  return (
    <>
      <div style={{ marginTop: "var(--spacing-2x)" }}>
        <MapboxStyleSelector
          name="configuration.map_box_style_id"
          label={t("admin.widgets.fields.mapstyle.label")}
          placeholder={t("admin.widgets.fields.mapstyle.placeholder")}
        />
      </div>

      <div style={{ marginTop: "var(--spacing-2x)" }}>
        <Select
          label="Bounding box preset"
          collection={presetCollection}
          value={baseLayerPreset}
          onValueChange={updateBaseLayerCoords}
        />
      </div>

      {[...Array(coordsCount).keys()].map((index) => (
        <Stack key={`coords${index}`} direction="row" gap={2}>
          <TextField
            name={`configuration.base_layer_coords[${index}].lat`}
            label={t("admin.widgets.fields.coords.lat.label")}
            type="number"
            required
            initialValue={initialValues["base_layer_coords"][index]?.["lat"]}
          />
          <TextField
            name={`configuration.base_layer_coords[${index}].lng`}
            label={t("admin.widgets.fields.coords.lng.label")}
            type="number"
            required
            initialValue={initialValues["base_layer_coords"][index]?.["lng"]}
          />
        </Stack>
      ))}

      <Stack gap={2} mt={2}>
        {[...Array(layerCount).keys()].map((index) => (
          <MapLayerConfiguration
            index={index}
            key={`layer${index}`}
            name={"configuration.layers"}
            label={t("admin.widgets.fields.map.type.label")}
            initialValue={initialValues["layers"][index] || DEFAULT_LAYER_VALUES}
            removeLayer={() => removeLayer(index)}
            layers={layers}
          />
        ))}

        <Button onClick={addLayer}>{t("admin.widgets.fields.map.layer.new")}</Button>
      </Stack>
    </>
  );
}
